import React from 'react';

import {
  CaraWorkspaceUseCase,
  SubscriptionEntitlement,
  SubscriptionPlan,
  type CustomFormListFieldsFragment,
  type CustomPresetFieldsFragment,
  type ExcelSpreadsheetTemplateFieldsFragment,
  type HtmlDocumentTemplateFieldsFragment,
  type LoggedInAgencyUserFieldsFragment,
  type WordDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { CREATE_CARA_WORKSPACE, usePermissions } from '~/components/permissions';
import { isPresetReady } from '~/components/presets/presets.utils';
import {
  customFormPickerOptions,
  excelTemplatePickerOptions,
  htmlTemplatePickerOptions,
  wordTemplatePickerOptions
} from '~/components/cara-input/template-picker-options';
import { hasRequiredLibrary, useCaseById, type UseCaseSpec } from './use-case-catalog';
import {
  usePinnedHomeItems,
  type AgencyProfileHomeScreenRules,
  type HomePin
} from '~/api/data/home/use-pinned-home-items';

export type HomeTile =
  | { pin: HomePin; preset: CustomPresetFieldsFragment }
  | { pin: HomePin; useCase: UseCaseSpec };

/**
 * Both the user's own arrangement and a profile's arrive in the same wire shape:
 * exactly one of the two ids is set, and a pin with neither is dropped.
 */
const toHomePins = (
  stored: ReadonlyArray<{ useCase?: CaraWorkspaceUseCase | null; customPresetId?: string | null }>
): HomePin[] =>
  stored.flatMap<HomePin>((pin) => {
    if (pin.useCase) {
      return [{ kind: 'useCase', id: pin.useCase }];
    }
    return pin.customPresetId ? [{ kind: 'customPreset', id: pin.customPresetId }] : [];
  });

export type HomeLauncherSources = {
  agencyUser: LoggedInAgencyUserFieldsFragment | null;
  customForms: CustomFormListFieldsFragment[];
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  wordDocumentTemplates: WordDocumentTemplateFieldsFragment[];
  excelSpreadsheetTemplates: ExcelSpreadsheetTemplateFieldsFragment[];
  customPresets: CustomPresetFieldsFragment[];
};

/**
 * What a surface that launches presets needs to know: which presets resolve and
 * can run, which libraries gate them, and the user's pins. Shared by the home
 * page and the client Overview so a pin reads the same on both.
 */
export const useHomeLauncher = (sources: HomeLauncherSources) => {
  // Every launch starts a workspace, so without CREATE there is nothing to run.
  const canCreate = usePermissions().hasAllPermissions(CREATE_CARA_WORKSPACE);

  // createCaraWorkspace rejects generating/failed presets, so a tile for one is a dead end.
  const runnablePresets = React.useMemo(
    () => sources.customPresets.filter(isPresetReady),
    [sources.customPresets]
  );
  const presetsByID = React.useMemo(
    () => new Map(runnablePresets.map((preset) => [preset.id, preset])),
    [runnablePresets]
  );

  // Standard plans keep Pro presets until they migrate, so plan alone is not the gate.
  const hasProAccess =
    sources.agencyUser?.agency?.subscription?.plan !== SubscriptionPlan.Basic ||
    !!sources.agencyUser?.agency?.entitlements.some(
      (entitlement) => entitlement === SubscriptionEntitlement.CopilotProPresets
    );

  const customFormOptions = React.useMemo(
    () => customFormPickerOptions(sources.customForms),
    [sources.customForms]
  );
  const htmlTemplateOptions = React.useMemo(
    () => htmlTemplatePickerOptions(sources.htmlDocumentTemplates),
    [sources.htmlDocumentTemplates]
  );
  const wordTemplateOptions = React.useMemo(
    () => wordTemplatePickerOptions(sources.wordDocumentTemplates),
    [sources.wordDocumentTemplates]
  );
  const excelTemplateOptions = React.useMemo(
    () => excelTemplatePickerOptions(sources.excelSpreadsheetTemplates),
    [sources.excelSpreadsheetTemplates]
  );

  const libraries = React.useMemo(
    () => ({
      word: wordTemplateOptions,
      html: htmlTemplateOptions,
      excel: excelTemplateOptions,
      customForm: customFormOptions
    }),
    [customFormOptions, excelTemplateOptions, htmlTemplateOptions, wordTemplateOptions]
  );

  // Shared by the tray, drawer, and write-back so a retired or library-gated pin
  // is dropped everywhere at once. Catalog entries stay so deep links still resolve.
  const resolvesPin = React.useCallback(
    (pin: HomePin) => {
      if (pin.kind === 'customPreset') {
        return presetsByID.has(pin.id);
      }
      const useCase = useCaseById(pin.id);
      return !!useCase && !useCase.unavailable && hasRequiredLibrary(useCase, libraries);
    },
    [libraries, presetsByID]
  );

  const storedPins = React.useMemo<HomePin[]>(
    () => toHomePins(sources.agencyUser?.preferences.homePins ?? []),
    [sources.agencyUser?.preferences.homePins]
  );

  // A profile only shapes the home screen while the plan includes profiles, so a
  // downgraded agency's users get their own arrangement back rather than being
  // held to one they cannot edit.
  const hasProfiles = !!sources.agencyUser?.agency?.entitlements.some(
    (entitlement) => entitlement === SubscriptionEntitlement.AgencyProfileManagement
  );
  const profile = hasProfiles ? sources.agencyUser?.profile : null;
  const homeScreenProfile = React.useMemo<AgencyProfileHomeScreenRules | undefined>(
    () =>
      profile
        ? {
            pins: toHomePins(profile.configuration.homeScreen.pins),
            allowUserCustomization: profile.configuration.homeScreen.allowUserCustomization
          }
        : undefined,
    [profile]
  );
  const askCaraEnabled = profile ? profile.configuration.homeScreen.askCaraEnabled : true;

  const pins = usePinnedHomeItems(storedPins, resolvesPin, homeScreenProfile);

  const tiles = React.useMemo<HomeTile[]>(
    () =>
      pins.pinned.flatMap<HomeTile>((pin) => {
        if (pin.kind === 'customPreset') {
          const preset = presetsByID.get(pin.id);
          return preset ? [{ pin, preset }] : [];
        }
        const useCase = useCaseById(pin.id);
        return useCase ? [{ pin, useCase }] : [];
      }),
    [pins.pinned, presetsByID]
  );

  return {
    canCreate,
    hasProAccess,
    runnablePresets,
    customFormOptions,
    htmlTemplateOptions,
    wordTemplateOptions,
    excelTemplateOptions,
    libraries,
    askCaraEnabled,
    pins,
    tiles
  };
};
